// src/ConsumerApp/Screens/ProductsList/components/ProductCard.js

import React, { useState, useEffect } from 'react';

// Real SVGs from assets/icons
import Bookmark from '../../../../assets/icons/bookmark.svg';
import FillBookmark from '../../../../assets/icons/FillBookmark.svg';
import WhitePlus from '../../../../assets/icons/WhitePlus.svg';
import WhiteMinus from '../../../../assets/icons/WhiteMinus.svg';
import ToteIcon from '../../../../assets/icons/tote.svg';

// Shared API Hooks
import {
  useAddItemToBucket,
  useUpdateBucketItemQuantity,
  useDeleteBucketItem,
} from '../../../api/products/product.hooks';

const IMAGE_BASE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

export default function ProductCard({
  item,
  customerId,
  hubId,
  savedBucketQuantity,
  bucketId,
  onItemClick,
  onOptionsClick,
  isSavedItem = false,
  onToggleSave,
}) {
  const productId = String(item?.product_id || item?._id);
  const name = item?.product_name || item?.name || 'Unknown Product';
  const imgPath = item?.product_image_url || item?.product_image || item?.image;
  const imageUrl = imgPath ? `${IMAGE_BASE_URL}${imgPath}` : null;

  const [imgError, setImgError] = useState(false);

  // Safe type-resilient purchase_mode checking
  const hasBuyNow = (() => {
    const pm = item?.purchase_mode;
    if (!pm) return false;
    if (Array.isArray(pm)) {
      return pm.some(
        (m) => typeof m === 'string' && m.toLowerCase().includes('buy now')
      );
    }
    if (typeof pm === 'string') {
      return pm.toLowerCase().includes('buy now');
    }
    return false;
  })();

  const isBuyNowAvailable = Boolean(hasBuyNow || item?.buy_now?.is_active);

  const buyNowPrice =
    item?.buy_now?.unit_price ||
    item?.selling_price ||
    item?.unit_price ||
    item?.price ||
    0;
  const mrpPrice = item?.mrp || 0;
  const discountText = item?.discount ? `${item.discount}% off` : '';

  const defaultQty = item?.buy_now?.min_qty || 1;
  const maxBuyNowQty = item?.buy_now?.max_qty || 999;
  const hasOptions = Number(item?.other_options_count || 0) > 0;

  const [isAdded, setIsAdded] = useState(savedBucketQuantity !== undefined);
  const [inputValue, setInputValue] = useState(
    savedBucketQuantity !== undefined ? String(savedBucketQuantity) : String(defaultQty)
  );

  // Production-grade Bookmark State
  const [localIsSaved, setLocalIsSaved] = useState(Boolean(isSavedItem));
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const addItemMutation = useAddItemToBucket();
  const updateItemMutation = useUpdateBucketItemQuantity();
  const deleteItemMutation = useDeleteBucketItem();

  useEffect(() => {
    setLocalIsSaved(Boolean(isSavedItem));
  }, [isSavedItem]);

  useEffect(() => {
    if (savedBucketQuantity !== undefined) {
      setIsAdded(true);
      setInputValue(String(savedBucketQuantity));
    } else {
      setIsAdded(false);
      setInputValue(String(defaultQty));
    }
  }, [savedBucketQuantity, defaultQty]);

  const handleInitialAdd = (e) => {
    e.stopPropagation();
    if (hasOptions && onOptionsClick) {
      onOptionsClick(item, isBuyNowAvailable);
      return;
    }

    setIsAdded(true);
    const initialQty = parseInt(inputValue, 10) || defaultQty;
    addItemMutation.mutate({
      customer_id: customerId,
      hubId: hubId,
      item_id: productId,
      purchase_type: 'BUY_NOW',
      quantity: initialQty,
    });
  };

  const handleIncrement = (e) => {
    e.stopPropagation();
    const current = parseInt(inputValue, 10) || 0;
    const next = current + 1;
    if (next <= maxBuyNowQty) {
      setInputValue(String(next));
      updateItemMutation.mutate({
        customer_id: customerId,
        hubId: hubId,
        item_id: productId,
        purchase_type: 'BUY_NOW',
        quantity: next,
      });
    }
  };

  const handleDecrement = (e) => {
    e.stopPropagation();
    const current = parseInt(inputValue, 10) || 0;
    const next = current - 1;

    if (next < defaultQty) {
      deleteItemMutation.mutate(
        { customer_id: customerId, item_id: productId, bucket_id: bucketId },
        {
          onSuccess: () => {
            setIsAdded(false);
            setInputValue(String(defaultQty));
          },
        }
      );
    } else {
      setInputValue(String(next));
      updateItemMutation.mutate({
        customer_id: customerId,
        hubId: hubId,
        item_id: productId,
        purchase_type: 'BUY_NOW',
        quantity: next,
      });
    }
  };

  const handleBlur = () => {
    let val = parseInt(inputValue, 10);
    if (isNaN(val) || val < 1) {
      deleteItemMutation.mutate(
        { customer_id: customerId, item_id: productId, bucket_id: bucketId },
        {
          onSuccess: () => {
            setIsAdded(false);
            setInputValue(String(defaultQty));
          },
        }
      );
      return;
    }
    if (val > maxBuyNowQty) val = maxBuyNowQty;
    setInputValue(String(val));
    updateItemMutation.mutate({
      customer_id: customerId,
      hubId: hubId,
      item_id: productId,
      purchase_type: 'BUY_NOW',
      quantity: val,
    });
  };

  const handleBookmark = async (e) => {
    e.stopPropagation();
    if (!onToggleSave || isSaveLoading) return;

    const targetState = !localIsSaved;
    setLocalIsSaved(targetState);
    setIsSaveLoading(true);

    try {
      const isSuccess = await onToggleSave(item, targetState);
      if (!isSuccess) {
        setLocalIsSaved(!targetState);
      }
    } catch {
      setLocalIsSaved(!targetState);
    } finally {
      setIsSaveLoading(false);
    }
  };

  return (
    <div style={styles.cardContainer} onClick={onItemClick}>
      {/* Top Image Section with Fallback and Bookmark Action */}
      <div style={styles.imageBox}>
        {imageUrl && !imgError ? (
          <img
            src={imageUrl}
            alt={name}
            style={styles.productImg}
            onError={() => setImgError(true)}
          />
        ) : (
          <div style={styles.fallbackBox}>
            <img src={ToteIcon} alt="fallback" style={styles.toteFallback} />
          </div>
        )}

        {/* Bookmark Trigger */}
        <button
          style={styles.saveBtn}
          onClick={handleBookmark}
          disabled={isSaveLoading}
          title={localIsSaved ? 'Saved to Shortlist' : 'Save for Later'}
          type="button"
        >
          <img
            src={localIsSaved ? FillBookmark : Bookmark}
            alt="save"
            style={{
              width: 17,
              height: 17,
              filter: localIsSaved
                ? 'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)'
                : 'none',
              transition: 'transform 0.15s ease',
            }}
          />
        </button>
      </div>

      {/* Floating Zepto-Style Stepper / Compact Add CTA */}
      <div style={styles.buttonFloatWrapper}>
        {!isAdded ? (
          <button style={styles.zeptoBtn} onClick={handleInitialAdd} type="button">
            <span style={styles.zeptoBtnText}>ADD TO BUY</span>
            {hasOptions && (
              <span style={styles.optionsBadge}>{item.other_options_count} Options</span>
            )}
          </button>
        ) : (
          <div style={styles.zeptoStepper} onClick={(e) => e.stopPropagation()}>
            <button
              style={styles.stepperActionBtn}
              onClick={handleDecrement}
              type="button"
              aria-label="Decrease quantity"
            >
              <img src={WhiteMinus} alt="-" style={{ width: 16, height: 16 }} />
            </button>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value.replace(/[^0-9]/g, ''))}
              onBlur={handleBlur}
              style={styles.stepperInput}
              maxLength={3}
              aria-label="Quantity"
            />
            <button
              style={styles.stepperActionBtn}
              onClick={handleIncrement}
              type="button"
              aria-label="Increase quantity"
            >
              <img src={WhitePlus} alt="+" style={{ width: 16, height: 16 }} />
            </button>
          </div>
        )}
      </div>

      {/* Details & Pricing */}
      <div style={styles.detailsBox}>
        <p style={styles.productTitle} title={name}>
          {name}
        </p>

        <div style={styles.labelRow}>
          <span style={styles.itemPriceLabel}>Item price</span>
          {discountText && <span style={styles.discountPill}>{discountText}</span>}
        </div>

        <div style={styles.priceRow}>
          <span style={styles.buyNowPrice}>₹{Number(buyNowPrice).toLocaleString('en-IN')}</span>
          {mrpPrice > 0 && mrpPrice !== buyNowPrice && (
            <span style={styles.mrpPrice}>₹{Number(mrpPrice).toLocaleString('en-IN')}</span>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  cardContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '0.5px solid #CBD5E1',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'visible',
    cursor: 'pointer',
    boxSizing: 'border-box',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  },
  imageBox: {
    width: '100%',
    height: 150,
    backgroundColor: '#FAFAF9',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  productImg: {
    maxHeight: '85%',
    maxWidth: '85%',
    objectFit: 'contain',
  },
  fallbackBox: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toteFallback: {
    width: 36,
    height: 36,
    opacity: 0.35,
  },
  saveBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: 14,
    width: 28,
    height: 28,
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  buttonFloatWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: -16,
    zIndex: 3,
    padding: '0 12px',
  },
  zeptoBtn: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E9001E',
    borderRadius: 8,
    padding: '6px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(233,0,30,0.15)',
    minWidth: 108,
  },
  zeptoBtnText: {
    fontSize: 11,
    fontWeight: '800',
    color: '#E9001E',
    letterSpacing: '0.4px',
  },
  optionsBadge: {
    fontSize: 9,
    fontWeight: '600',
    color: '#707B8C',
    marginTop: 1,
  },
  zeptoStepper: {
    backgroundColor: '#E9001E',
    border: 'none',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 4px',
    width: 104,
    height: 32,
    boxSizing: 'border-box',
    boxShadow: '0 2px 6px rgba(233, 0, 30, 0.28)',
  },
  stepperActionBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    width: 24,
    height: '100%',
  },
  stepperInput: {
    width: 32,
    border: 'none',
    background: 'transparent',
    textAlign: 'center',
    fontSize: 13,
    fontWeight: '800',
    color: '#FFFFFF',
    outline: 'none',
    padding: 0,
  },
  detailsBox: {
    padding: '12px 14px 14px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  productTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#2B2A29',
    lineHeight: '17px',
    height: 34,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    margin: '4px 0 0 0',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
  },
  itemPriceLabel: {
    fontSize: 11,
    color: '#707B8C',
  },
  discountPill: {
    backgroundColor: '#E8F5EE',
    color: '#1E8449',
    fontSize: 10,
    fontWeight: '700',
    padding: '1px 6px',
    borderRadius: 4,
  },
  priceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
  },
  buyNowPrice: {
    fontSize: 15,
    fontWeight: '700',
    color: '#2B2A29',
  },
  mrpPrice: {
    fontSize: 11,
    color: '#707B8C',
    textDecoration: 'line-through',
  },
};